import React, { forwardRef, type ForwardedRef } from "react";
import { useSelector } from "react-redux";
import { type KeyboardDatePickerProps } from "@material-ui/pickers";

import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import KeyboardDatePicker from "src/components/KeyboardDatePicker";
import {
  dateOnlyPickerWallDateToUtc,
  utcToDateOnlyPickerWallDate,
} from "src/utils/timezone";

/**
 * KeyboardDatePicker where the civil date is interpreted in the user's stored
 * timezone (picker `Date` is browser-local; see `timezone.ts` bridge helpers).
 */
const ZonedKeyboardDatePicker = forwardRef(function ZonedKeyboardDatePicker(
  props: Omit<KeyboardDatePickerProps, "value" | "onChange"> & {
    value: Date | null;
    onChange: (date: Date | null) => void;
  },
  ref: ForwardedRef<HTMLDivElement>
) {
  const { value, onChange, ...rest } = props;
  const timeZone = useSelector(effectiveUserTimeZoneSelector);

  const pickerValue =
    value == null
      ? null
      : isNaN(value.getTime())
      ? value
      : utcToDateOnlyPickerWallDate(value, timeZone);

  return (
    <KeyboardDatePicker
      {...rest}
      ref={ref}
      value={pickerValue}
      onChange={(d) => {
        if (d == null) {
          onChange(null);
          return;
        }
        if (isNaN(d.getTime())) {
          onChange(d);
          return;
        }
        onChange(dateOnlyPickerWallDateToUtc(d, timeZone));
      }}
    />
  );
});

export default ZonedKeyboardDatePicker as typeof KeyboardDatePicker;
